import {
  asyncStorageKeys,
  defaultMaleProfileUrl,
  userMetaDataKeys,
} from '@inbox-demo-nx/constants';
import { ScenarioData, SendApiRequest } from '@inbox-demo-nx/types';
import analytics from '@inbox-demo-nx/utils-analytics';
import PlatformAPI from '@inbox-demo-nx/utils-platform-api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AppState, AppStateStatus } from 'react-native';
import SendBird from 'sendbird';
import { v4 as uuidv4 } from 'uuid';

import resetUser from './resetUser';

const DEFAULT_FEMALE_PROFILE_URL =
  'https://file-us-3.sendbird.com/61b3540cce934d1cbf5eb1e980f63d15.jpg';

async function getUserFromAsyncStorage() {
  try {
    const user = await AsyncStorage.getItem(asyncStorageKeys.savedUser);
    return user && JSON.parse(user);
  } catch {
    return null;
  }
}

async function saveUserToAsyncStorage(user: unknown) {
  try {
    if (user == null) {
      await AsyncStorage.removeItem(asyncStorageKeys.savedUser);
    } else {
      await AsyncStorage.setItem(
        asyncStorageKeys.savedUser,
        JSON.stringify(user)
      );
    }
  } catch {
    // ignore
  }
}

const deepCopy = (obj: unknown) => JSON.parse(JSON.stringify(obj));

const useApp = ({
  appId,
  scenario,
  queryClient,
  apiUrl,
  onUserInitialized,
}: {
  appId: string;
  scenario: ScenarioData;
  queryClient: QueryClient;
  apiUrl?: string;
  onUserInitialized?: (user: SendBird.User) => void;
}) => {
  const handleUserInitializedEvent = useRef(onUserInitialized);

  useEffect(() => {
    handleUserInitializedEvent.current = onUserInitialized;
  }, [onUserInitialized]);

  const sendApiRequest = useCallback<SendApiRequest>(
    async (options) => {
      if (apiUrl) {
        const { data } = await axios.post(apiUrl, options);
        return data;
      } else {
        const { type, data } = options;
        // eslint-disable-next-line @typescript-eslint/ban-types
        return (PlatformAPI[type as keyof typeof PlatformAPI] as Function)(
          data
        );
      }
    },
    [apiUrl]
  );

  const sendbird = useMemo(() => {
    const sbInstance = new SendBird({ appId });
    sbInstance.useAsyncStorageAsDatabase(AsyncStorage);
    return sbInstance;
  }, [appId]);

  const connectToSendBird = useCallback(
    async ({
      username,
      nickname,
      gender,
    }: {
      username: string;
      nickname?: string;
      gender?: 'male' | 'female';
    }) => {
      let user = await sendbird.connect(username);

      if (nickname) {
        user = await sendbird.updateCurrentUserInfo(
          nickname,
          gender === 'female'
            ? DEFAULT_FEMALE_PROFILE_URL
            : defaultMaleProfileUrl
        );
        return user;
      }

      console.info('connected to sendbird');
      return user;
    },
    [sendbird]
  );

  const [currentUser, setCurrentUser] = useState<SendBird.User | null>(null);
  const [isInitialUserLoaded, setInitialUserLoaded] = useState(false);
  const [isInitializingUser, setIsInitializingUser] = useState(false);

  const updateCurrentUserState = useCallback(async () => {
    const user = sendbird.currentUser;
    await saveUserToAsyncStorage(user);
    setCurrentUser(deepCopy(user));
  }, [sendbird.currentUser]);

  const initializeCurrentUser = useCallback(async () => {
    setIsInitializingUser(true);

    const userId = sendbird.currentUser.userId;
    const nickname = sendbird.currentUser.nickname;
    try {
      await sendbird.disconnect();
      queryClient.clear();

      await resetUser({ userId, scenario, sendApiRequest });
      const user = await connectToSendBird({
        username: userId,
        nickname: scenario.userProfile?.nickname,
      });
      await updateCurrentUserState();
      handleUserInitializedEvent.current?.(user);

      setIsInitializingUser(false);
    } catch (error) {
      console.error(error);
      Alert.alert('Initialization failed.');
      await sendbird.disconnect();
      await connectToSendBird({ username: userId, nickname });
      await updateCurrentUserState();
    } finally {
      setIsInitializingUser(false);
    }
  }, [
    connectToSendBird,
    queryClient,
    scenario,
    sendApiRequest,
    sendbird,
    updateCurrentUserState,
  ]);

  const setUpNewUser = useCallback(async () => {
    setIsInitializingUser(true);
    try {
      // create a new user and sign in
      const userId = uuidv4();
      await connectToSendBird({
        username: userId,
        nickname: scenario.userProfile?.nickname,
        gender: 'male',
      });

      // initialize user
      await resetUser({ userId, scenario, sendApiRequest });
      await updateCurrentUserState();
    } catch (error) {
      console.error(error);
    } finally {
      setIsInitializingUser(false);
    }
  }, [connectToSendBird, scenario, sendApiRequest, updateCurrentUserState]);

  const authContextValue = useMemo(() => {
    return {
      currentUser,
      isInitializingUser,
      updateCurrentUserState,
      setIsInitializingUser,
      initializeCurrentUser,
      setUpNewUser,
    };
  }, [
    currentUser,
    initializeCurrentUser,
    isInitializingUser,
    setUpNewUser,
    updateCurrentUserState,
  ]);

  useEffect(() => {
    async function prepare() {
      try {
        const user = await getUserFromAsyncStorage();

        if (user) {
          setCurrentUser(user);
          if (!sendbird.currentUser) {
            const latestUser = await connectToSendBird({
              username: user.userId,
              nickname: user.nickname,
            });
            setCurrentUser(latestUser);

            const userScenarioVersion =
              latestUser.metaData[userMetaDataKeys.initializedVersion];
            if (userScenarioVersion !== scenario.version) {
              console.info(
                `Scenario has been updated: ${userScenarioVersion} → ${scenario.version}`
              );
              await initializeCurrentUser();
            }
          }
        } else {
          await setUpNewUser();
        }
      } catch (e) {
        console.warn(e);
      } finally {
        setInitialUserLoaded(true);
      }
    }
    prepare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleStateChange = (newState: AppStateStatus) => {
      if (newState === 'active') {
        sendbird.setForegroundState();
      } else {
        sendbird.setBackgroundState();
      }
    };

    const appStateListener = AppState.addEventListener(
      'change',
      handleStateChange
    );

    return () => {
      appStateListener.remove();
    };
  }, [sendbird]);

  useEffect(() => {
    if (currentUser?.userId) {
      analytics.identify(`${scenario.key}-${currentUser?.userId}`, {
        demo_key: scenario.key,
      });
    }
  }, [currentUser?.userId, scenario.key]);

  const isLoadingUser = !isInitialUserLoaded || isInitializingUser;

  return {
    authContextValue,
    isLoadingUser,
    isInitialUserLoaded,
    sendbird,
    sendApiRequest,
  };
};

export default useApp;
